import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../../i18n';
import { change, Field, formValueSelector, reduxForm, initialize } from 'redux-form';
import { useSelector } from 'react-redux';
import { string } from 'yup';
import { FormControlLabel, makeStyles, Box, Grid, Hidden } from '@material-ui/core';

import { makeAddressValidator, customUrlWithReservedNamesValidator } from 'core/validators/validators';
import { useFormError, validator } from 'core/form/form';
import { useMounted } from 'core/hooks/useMounted';
import { useAction, useActionDispatchOnMount } from 'core/store/store';

import { authPhoneNumberSelector, isAuthUserFakeStatusSelector } from 'state/auth/selectors';
import { makeOnboardingFetchStoreAction } from 'state/onboarding/actions';
import { onboardingStoreFormDataSelector } from 'state/onboarding/selectors';

import { ErrorBoundary } from 'components/ErrorBoundary';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { FormAddress } from 'components/FormAddress/FormAddress';
import { SelectEnumControlField } from 'components/SelectEnumControl/SelectEnumControl';
import { TextControlField } from 'components/TextControl/TextControl';
import { Text, TextTypography } from 'components/Text/Text';

import { Form } from 'forms/types';
import { AddressDto, CroppedArea, StoreCategory } from 'types';

import { PromoVideo } from './PromoVideo';
import { DefaultFooter } from './DefaultFooter';

import { StoreFormData } from './types';
import { getClientUrl, transliterationForUrl } from 'core/utils/utils';
import { Checkbox } from '../../components/Checkbox/Checkbox';
import { GalleryControlField } from 'components/Gallery/GalleryControlField';
import { makeUserMenuProfileFetchAction } from 'state/userMenu/actions';
import { userMenuProfileSelector } from 'state/userMenu/selector';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { TranslatableEnum } from '../../components/T/GetTranslationForEnum';
import { MobilePhoneControlField } from '../../components/MobilePhoneControl/MobilePhoneControl';
import { appConfig } from 'config/app';

export interface StoreFormProps {
    isCreateMode: boolean;
    onImageCrop: (crop: CroppedArea) => void;
}

const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1.25, 1.25, 0, 0),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        [theme.breakpoints.down('md')]: {
            borderRadius: 0,
        },
    },
    main: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(3),
    },
    header: {
        paddingBottom: theme.spacing(3),
    },
    title: {
        marginBottom: theme.spacing(1),
    },
    panes: {
        display: 'flex',
        flex: 1,
        padding: theme.spacing(3, 0),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
    leftPane: {
        flex: 1,
    },
    rightPane: {
        flex: 1,
        paddingLeft: theme.spacing(9),
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
    },
    fieldGroup: {
        borderBottom: `1px solid ${theme.palette.brandPrimary.lightest}`,
    },
    field: {
        marginBottom: theme.spacing(3),
    },
    footer: {
        flexBasis: 'auto',
    },
    notificationBlock: {
        marginTop: theme.spacing(1),
    },
}));

// TODO:
const initialValues: any = {
    coordinates: { lat: null as number, lon: null as number },
    address: '',
    waPhone: true,
};

const storeCategoryOptions = [
    StoreCategory.PRODUCTS,
    StoreCategory.RESTAURANTS,
    StoreCategory.FLOWERS,
    StoreCategory.FOR_HOME,
    StoreCategory.FOR_CHILDREN,
    StoreCategory.CLOTHS_SHOES,
    StoreCategory.PETS,
    StoreCategory.BEAUTY_HEALTH,
    StoreCategory.BUILDING,
    StoreCategory.FURNITURE,
    StoreCategory.SERVICES,
    StoreCategory.SPORTS,
    StoreCategory.EDUCATION,
    StoreCategory.OTHER,
];

const formSelector = formValueSelector(Form.ONBOARDING_STORE_FORM);
const phoneSelector = <T extends {}>(state: T) => formSelector(state, 'phone');
const coordinatesSelector = <T extends {}>(state: T) => formSelector(state, 'coordinates');
const nameSelector = <T extends {}>(state: T) => formSelector(state, 'name');

export const Store = reduxForm<StoreFormData, StoreFormProps>({
    form: Form.ONBOARDING_STORE_FORM,
    validate: validator({
        name: string().required('common:validation.required'),
        storeCategory: string().nullable().required('common:validation.required'),
        phone: string()
            .nullable()
            .required('common:validation.required')
            .test({
                message: 'common:validation.phoneFormat',
                test: value => {
                    if (!value) {
                        return true;
                    }
                    return isPossiblePhoneNumber(value);
                },
            }),
        customUrl: customUrlWithReservedNamesValidator,
    }),
    asyncValidate: makeAddressValidator(Form.ONBOARDING_STORE_FORM, 'address'),
    asyncChangeFields: ['address', 'coordinates'],
})(props => {
    const { form, handleSubmit } = props;
    const action = useAction();
    const { t } = i18n.useTranslation();
    const error = useFormError(Form.ONBOARDING_STORE_FORM);

    const isFakeRegistration = useSelector(isAuthUserFakeStatusSelector);
    useActionDispatchOnMount(true, makeOnboardingFetchStoreAction);

    useActionDispatchOnMount(!isFakeRegistration, makeUserMenuProfileFetchAction);
    const userProfile = useSelector(userMenuProfileSelector);

    const userProfileEmail = useMemo(() => (isFakeRegistration ? null : userProfile?.email), [
        userProfile?.email,
        isFakeRegistration,
    ]);

    const emailInfo = useMemo(() => userProfileEmail || t('onboarding:storeDetails.emptyEmailInfo'), [
        userProfileEmail,
        t,
    ]);

    const classes = useStyles();

    const selectedAccPhoneNumber = useSelector(authPhoneNumberSelector)
    const accountPhoneNumber = isFakeRegistration ? null : selectedAccPhoneNumber;

    const storePhoneNumber = useSelector(phoneSelector);

    const nameData = useSelector(nameSelector);

    // не прописывать для отслеживания action, form
    useEffect(() => {
        if (nameData) {
            const url = transliterationForUrl(nameData);
            action(change, form, 'customUrl', url);
        }
    }, [nameData]);

    const initialStoreFormData = useSelector(onboardingStoreFormDataSelector);
    useEffect(() => {
        action(initialize, form, initialStoreFormData);
    }, [initialStoreFormData]);

    const coordinates = useSelector(coordinatesSelector);

    const handleCoordinatesChange = useCallback(
        (coordinates: { lat: number; lon: number }) => {
            action(change, form, 'coordinates', coordinates);
        },
        [action, form],
    );

    const handleStandardAddressChange = useCallback(
        (data: AddressDto) => {
            action(change, form, 'standardAddress', data);
        },
        [action, form],
    );

    const [isSamePhone, setIsSamePhone] = useState(true);
    const [phone, setPhone] = useState(accountPhoneNumber);
    const handlePhoneChange = useCallback((phoneNumber: any) => setPhone(phoneNumber), [setPhone]);

    useMounted(() => {
        action(change, form, 'phone', accountPhoneNumber);
        action(change, form, 'waPhone', true);
    });

    const handleSamePhoneToggle = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            if (checked) {
                action(change, form, 'phone', accountPhoneNumber);
            }
            setIsSamePhone(checked);
        },
        [action, setIsSamePhone],
    );

    const [isWAPhone, setIsWAPhone] = useState(true);
    const handleChangePhoneWAStatus = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            action(change, form, 'waPhone', checked);
            setIsWAPhone(checked);
        },
        [action, setIsWAPhone],
    );

    const [isEmailNotification, setIsEmailNotification] = useState(true);
    const handleSwitchEmailNotification = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            action(change, form, 'emailNotification', checked);
            setIsEmailNotification(checked);
        },
        [action, setIsEmailNotification],
    );

    useEffect(() => {
        const emailExist = !!userProfileEmail;
        setIsEmailNotification(emailExist);
        action(change, form, 'emailNotification', emailExist);
    }, [userProfileEmail, setIsEmailNotification]);

    const [isSmsNotification, setIsSmsNotification] = useState(false);
    const handleSwitchSmsNotification = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            action(change, form, 'smsNotification', checked);
            setIsSmsNotification(checked);
        },
        [action, setIsSmsNotification],
    );

    useEffect(() => {
        setIsSamePhone(accountPhoneNumber === storePhoneNumber);
    }, [accountPhoneNumber, storePhoneNumber, setIsSamePhone]);

    const phoneControlValidator = useCallback(
        (v: string) => {
            if ((isFakeRegistration && !v) || (!isFakeRegistration && !isSamePhone && !v)) {
                return t('common:validation.required');
            }
        },
        [isFakeRegistration, t, isSamePhone],
    );

    return (
        <ErrorBoundary>
            <form className={classes.container} onSubmit={handleSubmit}>
                <main className={classes.main}>
                    <header className={classes.header}>
                        <Box mb={2}>
                            <Text className={classes.title} typography={TextTypography.TITLE}>
                                {t('onboarding:store.title')}
                            </Text>
                            <Text typography={TextTypography.BODY}>{t('onboarding:store.description')}</Text>
                        </Box>
                        <Grid item xs={12}>
                            <Box>
                                <Hidden smDown>
                                    <Box p={0.5} mb={2}>
                                        <Text typography={TextTypography.TITLE}>
                                            {t('onboarding:storeDetails.gallery')}
                                        </Text>
                                    </Box>
                                </Hidden>
                                <Field name="gallery" component={GalleryControlField} />
                            </Box>
                        </Grid>
                    </header>
                    <div className={classes.panes}>
                        <div className={classes.leftPane}>
                            <div className={classes.fieldGroup}>
                                <div className={classes.field}>
                                    <Field
                                        name="name"
                                        component={TextControlField}
                                        label={t('onboarding:storeDetails.name')}
                                    />
                                </div>
                                <div className={classes.field}>
                                    <Field
                                        name="address"
                                        component={FormAddress}
                                        label={t('common:addressControl.streetAddress')}
                                        initWithGeolocationData={props.isCreateMode}
                                        initialCoordinates={initialValues.coordinates}
                                        value={initialValues.address}
                                        buttonText="OK"
                                        onCoordinatesChange={handleCoordinatesChange}
                                        onStandardAddressChange={handleStandardAddressChange}
                                        headerText={t('common:addressDialog.enterShopAddress')}
                                        coordinates={coordinates}
                                    />
                                </div>
                                <div className={classes.field}>
                                    <Field
                                        name="storeCategory"
                                        component={SelectEnumControlField}
                                        label={t('onboarding:storeDetails.storeCategory')}
                                        enum={TranslatableEnum.STORE_CATEGORY}
                                        items={storeCategoryOptions}
                                    />
                                </div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <div className={classes.field}>
                                    <Field
                                        name="phone"
                                        component={MobilePhoneControlField}
                                        validate={phoneControlValidator}
                                        label={t('onboarding:storeDetails.phone')}
                                        onChange={handlePhoneChange}
                                    />
                                    {!isFakeRegistration && (
                                        <FormControlLabel
                                            value="end"
                                            control={
                                                <Checkbox
                                                    checked={isSamePhone}
                                                    name="isSamePhone"
                                                    onChange={handleSamePhoneToggle}
                                                />
                                            }
                                            label={
                                                <Text typography={TextTypography.BODY}>
                                                    {t('onboarding:storeDetails.checkboxPhone')}
                                                </Text>
                                            }
                                        />
                                    )}
                                    <FormControlLabel
                                        value="waPhone"
                                        control={
                                            <Checkbox
                                                checked={isWAPhone}
                                                name="waPhone"
                                                onChange={handleChangePhoneWAStatus}
                                            />
                                        }
                                        label={
                                            <Text typography={TextTypography.BODY}>
                                                {t('onboarding:storeDetails.waPhone')}
                                            </Text>
                                        }
                                    />
                                    <Text className={classes.notificationBlock} typography={TextTypography.BODY}>
                                        {t('onboarding:storeDetails.notificationInfo')}
                                    </Text>
                                    <FormControlLabel
                                        value="emailNotification"
                                        disabled={!userProfileEmail}
                                        control={
                                            <Checkbox
                                                checked={isEmailNotification}
                                                name="emailNotification"
                                                onChange={handleSwitchEmailNotification}
                                            />
                                        }
                                        label={
                                            <Text
                                                typography={
                                                    userProfileEmail ? TextTypography.BODY : TextTypography.BODY_LIGHT
                                                }
                                            >
                                                {t('onboarding:storeDetails.emailNotification', { emailInfo })}
                                            </Text>
                                        }
                                    />
                                    <FormControlLabel
                                        value="smsNotification"
                                        control={
                                            <Checkbox
                                                checked={isSmsNotification}
                                                name="smsNotification"
                                                onChange={handleSwitchSmsNotification}
                                            />
                                        }
                                        label={
                                            <Text typography={TextTypography.BODY}>
                                                {t('onboarding:storeDetails.smsNotification', { phoneInfo: phone })}
                                            </Text>
                                        }
                                    />
                                </div>
                            </div>
                            <div className={classes.field}>
                                <Field
                                    name="customUrl"
                                    component={TextControlField}
                                    autoCapitalize="none"
                                    label={t('onboarding:storeDetails.customUrl')}
                                    adornmentStart={`${new URL(getClientUrl()).host}/`}
                                />
                            </div>
                            {error && <FormErrorMessage error={error} />}
                            <div className={classes.field}>
                                <Field
                                    name="domain"
                                    component={TextControlField}
                                    autoCapitalize="none"
                                    label={t('onboarding:storeDetails.domain')}
                                />
                                <Text typography={TextTypography.BODY}>{t('onboarding:storeDetails.domainInfo')}</Text>
                            </div>
                        </div>
                        <div className={classes.rightPane}>
                            <PromoVideo />
                        </div>
                    </div>
                </main>
                <footer className={classes.footer}>
                    <DefaultFooter />
                </footer>
            </form>
        </ErrorBoundary>
    );
});
